import { useState, useEffect, useMemo } from 'react';

import { Box, Spinner } from '@frontend/components';

import ExpandIcon from './subcomponents/ExpandIcon';
import FooterBlock, { hasFooter } from './subcomponents/FooterBlock';
import SelectionWrapper from './subcomponents/SelectionWrapper';
import StyledRCTable from './subcomponents/StyledRCTable';
import SummaryBlock, { hasSummary } from './subcomponents/SummaryBlock';
import TablePagination from './subcomponents/TablePagination';
import TitleBlock, { hasTitle } from './subcomponents/TitleBlock';
import { TableProvider } from './utils/TableContext';
import applyAllStyling from './utils/applyAllStyling';
import collapseOthersExpand from './utils/expandHelpers';
import translateColumns from './utils/translateColumns';

// CUSTOM COMPONENTS w/ RC TABLE TUTORIAL --> https://github.com/react-component/table/pull/171

/**
 * TABLE COMPONENT -
 * A wrapper around rc-table with limitations to modifying its styling and
 * functionality suited to Paperless' unique needs.
 *
 * @deprecated use TableV2 from '@frontend/components' instead.
 */
export const OldTable = ({
  variant = 'default',
  columns,
  data,
  onChange, // function that outputs the current table state to be passed to the backend
  loading = false,
  tableLayout = 'auto',
  // @TODO: Need to fix how scroll works, by building my own version of vertical scroll..
  // ...To avoid the issue from rc-table where it creates a separate table for the scroll section...
  // ...And thus the header won't use the same spacing as the body.
  scroll = {
    x: 1,
    y: undefined,
  },
  showHeader = true,
  // @TODO: Remove this and replace with variant system
  styleOptions = {
    headerColumnDividers: true,
    bodyColumnDividers: true,
    summaryColumnDividers: true,
  },
  headerOptions = {
    title: null,
    leftComponent: null,
    rightComponent: null,
    customHeader: null,
  },
  expandedRowKeys = [],
  setExpandedRowKeys,
  expandAllowMultiple = false,
  expandClickAnywhere = false,
  expandRenderer = () => null,
  expandIconColumnIndex = 0,
  // Select single select: make the selection use radios instead of checkboxes
  selectionSingleSelect = false,
  // Selection hide global: if we have selection and aren't in single select, hide global selection.
  selectionHideGlobal = false,
  // onSelect: a callback to be called whenever the current selection changes.
  // If this prop is added, checkboxes will be added to the rows.
  onSelect,
  // selected: a state for storing selected content.
  selected,
  onRow,
  paginationOptions = {
    totalItems: null,
    itemsPerPage: null,
    initialPage: 1,
  },
  footerOptions = {
    leftComponent: null,
    rightComponent: null,
    customFooter: null,
  },
  emptyOptions = {
    emptyLabel: 'no data',
    customEmpty: null,
  },
  rowKey,
  ...restProps
}) => {
  // ORDER OF DATA TRANSFORMATION FOR ONCHANGE CALLBACK:
  // raw data -> filter -> sort -> paginate -> view

  const [currentPage, setCurrentPage] = useState(
    paginationOptions?.initialPage || 1,
  );

  // Column translation: A function that splits our custom column input (columns) into:
  // - an RC-table compatible array (translatedColumns)
  // - an internal state object for storing filters and sorting (tempFilterConfigs)
  // We're memoizing this since the temp variables used as state initial values shouldn't change if columns doesn't change.
  const { tempTranslatedColumns, tempFilterConfigs } = useMemo(
    () =>
      translateColumns(
        columns,
        onSelect,
        selectionSingleSelect,
        selectionHideGlobal,
        tableLayout,
      ),
    [
      columns,
      onSelect,
      selectionSingleSelect,
      selectionHideGlobal,
      tableLayout,
    ],
  );

  // Filter configs: a store of the current filtered state as an object with keys for each column...
  // ... where each column contains the current sort direction, available sort directions, and current filter state for the column.
  const [filterConfigs, setFilterConfigs] = useState(tempFilterConfigs);
  const [translatedColumns, setTranslatedColumns] = useState(
    tempTranslatedColumns,
  );

  useEffect(() => {
    setTranslatedColumns(tempTranslatedColumns);
    setFilterConfigs(tempFilterConfigs);
  }, [columns, tempTranslatedColumns, tempFilterConfigs]);

  // Whenever the filterConfigs update, deliver the changes out of the component.
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    if (filterConfigs != null) {
      applyAllStyling(filterConfigs);
      onChange?.({
        filters: filterConfigs,
        page: paginationOptions?.initialPage || 1,
      }); // deliver the change,
      setCurrentPage(paginationOptions?.initialPage || 1); // and reset the pagination.
    }
  }, [filterConfigs, onChange, paginationOptions.initialPage]);
  /* -------------------------------------------------------------------------- */

  // Whenever the selection mode changes, reset the current selection to empty.
  useEffect(() => {
    onSelect?.(selectionSingleSelect ? null : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionSingleSelect]);

  /* Helper function to check if the table needs an expand column.
   * Returns true if we have data and also have a custom expand renderer or children.
   * Returns false if there is no data.
   */
  const canExpand = (data) => {
    return data != null
      ? (data?.some((record) => record.children != null) ?? true) ||
          (data?.some(
            (record, index) => expandRenderer(record, index) != null,
          ) ??
            true)
      : false;
  };

  const borderless = variant === 'borderless';

  return (
    <Box
      w="100%"
      position="relative"
      border={borderless ? 'none' : '1px solid'}
      borderColor="separator.border"
      borderRadius={borderless ? 'none' : 'lg'}
      overflow={'hidden'}
      {...restProps}
    >
      <TableProvider
        value={{
          data,
          filterConfigs,
          setFilterConfigs,
        }}
      >
        <SelectionWrapper
          selected={selected}
          onSelect={onSelect}
          useRadios={selectionSingleSelect}
        >
          {loading ? (
            <Box
              alignItems="center"
              display="flex"
              cursor="wait"
              height="100%"
              justifyContent="center"
              left="0"
              position="absolute"
              top="0"
              w="100%"
              zIndex={10}
            >
              <Spinner size="lg" />
            </Box>
          ) : null}

          <StyledRCTable
            rowKey={rowKey}
            tableLayout={tableLayout}
            loading={loading}
            showHeader={showHeader}
            styleOptions={styleOptions}
            title={
              hasTitle(headerOptions)
                ? headerOptions.customHeader != null
                  ? () => headerOptions.customHeader
                  : // eslint-disable-next-line react/display-name
                    () => (
                      <TitleBlock
                        title={headerOptions.title || null}
                        leftComponent={headerOptions.leftComponent || null}
                        rightComponent={headerOptions.rightComponent || null}
                      />
                    )
                : null
            }
            columns={translatedColumns}
            data={data}
            scroll={scroll}
            emptyText={
              emptyOptions.customEmpty != null
                ? () => emptyOptions.customEmpty
                : emptyOptions.emptyLabel
            }
            direction={'ltr'}
            expandable={
              canExpand(data)
                ? {
                    defaultExpandedRowKeys: [],
                    // expandedRowKeys: an array that holds which rows are currently expanded.
                    /* ----------------------------------- */
                    expandedRowKeys:
                      expandAllowMultiple == false ? expandedRowKeys : null,
                    /* ----------------------------------- */

                    // expandedRowRender: a custom render function based on the record...
                    /* ----------------------------------- */
                    expandedRowRender: expandRenderer,
                    /* ----------------------------------- */

                    // expandedRowClassName: a class name added to expanded rows...
                    // ... in our case, a class name that hides the expanded row for any rows without expanded content...
                    // ... fixing an issue where an empty expanded row was shown in between a parent and their children.
                    // NOTE: a corresponding class in the stylesheet actually hides the row with display: none.
                    /* ----------------------------------- */
                    expandedRowClassName: (record, index) => {
                      if (expandRenderer?.(record, index) != null) {
                        return '';
                      } else {
                        return 'hideExpandedContentRow';
                      }
                    },
                    /* ----------------------------------- */

                    // expandRowByClick: determines whether you should be able to click the entire row to expand or not.
                    /* ----------------------------------- */
                    expandRowByClick: expandClickAnywhere,
                    /* ----------------------------------- */

                    // rowExpandable: determines whether or not a row should receive an expand icon.
                    rowExpandable: (record, index) =>
                      record.children != null ||
                      expandRenderer?.(record, index) != null,
                    // onExpand: a function that runs whenever a row is expanded by the user...
                    // ... in our case, used to run a function to close other rows when
                    onExpand:
                      expandAllowMultiple === false
                        ? (expanded, record) =>
                            setExpandedRowKeys(
                              collapseOthersExpand(
                                expanded,
                                record,
                                data,
                                expandedRowKeys,
                              ),
                            )
                        : null,

                    // eslint-disable-next-line react/display-name
                    expandIcon: (props) => <ExpandIcon {...props} />,
                    expandIconColumnIndex: expandIconColumnIndex,
                  }
                : null
            }
            footer={
              hasFooter(footerOptions, paginationOptions)
                ? footerOptions.customFooter != null
                  ? () => footerOptions.customFooter
                  : // eslint-disable-next-line react/display-name
                    () => (
                      <FooterBlock
                        leftComponent={footerOptions.leftComponent || null}
                        rightComponent={footerOptions.rightComponent || null}
                        paginationComponent={
                          paginationOptions.itemsPerPage != null ? (
                            <TablePagination
                              paginationOptions={paginationOptions}
                              currentPage={currentPage}
                              setCurrentPage={setCurrentPage}
                              onChange={onChange}
                            />
                          ) : null
                        }
                      />
                    )
                : null
            }
            onRow={onRow != null ? onRow : null}
            /* Assemble the final class name based on input className...
           as well as the presence of children and/or expanded content.*/
            rowClassName={(record, index) => {
              let className = '';
              if (
                record.children != null ||
                expandRenderer?.(record, index) != null
              ) {
                className += ' canExpand';
              }
              if (selectionSingleSelect) {
                if (selected === record.key) {
                  className += ' rowSelected';
                }
              } else {
                if (selected?.includes(record.key)) {
                  className += ' rowSelected';
                }
              }
              if (onRow?.(record, index).onClick != null) {
                className += ' canClick';
              }

              return className;
            }}
            summary={
              hasSummary(columns)
                ? // eslint-disable-next-line react/display-name
                  () => (
                    <SummaryBlock
                      columns={columns}
                      canExpand={canExpand(data)}
                    />
                  )
                : null
            }
          />
        </SelectionWrapper>
      </TableProvider>
    </Box>
  );
};
